// src/oura/lib/questionsCsv.ts
//
// The per-question rows of one scan as a CSV (#3786), so an Oura scan can be
// compared with a manual measurement question by question instead of by
// totals. Built in the browser from `results.probes[]` — no backend call.

import type { AeoGoogleAiReading, AeoProbe } from "@service/aeo";

const COLUMNS = ["question", "engine", "instrument", "mentioned", "rivals", "cited_pages"] as const;

/**
 * One cell, RFC 4180: quoted when it holds a comma, quote, or line break, with
 * quotes doubled.
 *
 * A leading `=`, `+`, `-` or `@` is prefixed with `'` first. Questions, rival
 * names and engine output are text nobody here wrote, and Excel and Sheets
 * both run a cell that starts that way as a formula.
 */
function cell(value: string | number): string {
    let s = String(value);
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * One row per question × engine: 2 engines × 50 questions = 100 rows, plus
 * Google's 50 when the scan read them.
 *
 * Google's rows come from `googleAiReadings`, not `probes`, because the
 * backend has never written a Google row into `probes` — the export simply had
 * no Google in it at all.
 */
export function buildQuestionsCsv(probes: AeoProbe[], googleAiReadings: AeoGoogleAiReading[] = []): string {
    const probeRows = probes.map((p) => [
        p.query,
        p.engine,
        // Per probe, not per run: one run can mix app and API answers. Empty
        // when the probe does not say — never a guess from the run's label.
        p.instrument ?? "",
        p.mentioned ? "yes" : "no",
        // The merged names first, the same names the chips and the
        // competitors table show (#3789).
        (p.rivalNames ?? p.rivals ?? []).join("; "),
        (p.cited_pages ?? []).length,
    ].map(cell).join(","));

    /**
     * Google's rows, from `googleAiReadings` rather than `probes` — the
     * backend has never written a Google row into `probes`, so the export had
     * no Google in it at all.
     *
     * `mentioned` is the whole verdict here: a question Google showed no
     * overview for, and one whose read failed, are both "no" (product decision
     * on #3945, 30 Sep). The first version carried a fourth column naming
     * which of the three it was; with only two states left it would repeat
     * `mentioned` in words, so the export keeps its original six columns and
     * nothing reading them positionally has to change.
     */
    const googleRows = googleAiReadings.map((r) => [
        r.query,
        r.engine || "google_ai",
        r.instrument || "google_serp",
        // A page still being read has no verdict yet: blank, never "no"
        // (ourteam-backend#5619).
        r.pending === true ? "" : r.shown === true && r.mentioned === true ? "yes" : "no",
        // Only an overview Google actually showed can have named a rival.
        r.shown === true ? (r.rivalNames ?? r.rivals ?? []).join("; ") : "",
        (r.sources ?? []).length,
    ].map(cell).join(","));

    const rows = [...probeRows, ...googleRows];
    // CRLF and a BOM: without the BOM, Excel reads UTF-8 as ANSI and mangles
    // every non-ASCII business or rival name.
    return "﻿" + [COLUMNS.join(","), ...rows].join("\r\n");
}

/** `<business-name>_<scan-date>_questions.csv`, with the name made filename-safe. */
export function questionsCsvFilename(businessName: string, capturedAt: string | null | undefined): string {
    const name = businessName.trim().replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, "-") || "business";
    const date = capturedAt?.slice(0, 10) || "undated";
    return `${name}_${date}_questions.csv`;
}

export function downloadQuestionsCsv(
    probes: AeoProbe[],
    businessName: string,
    capturedAt: string | null | undefined,
    googleAiReadings: AeoGoogleAiReading[] = [],
): void {
    const blob = new Blob([buildQuestionsCsv(probes, googleAiReadings)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = questionsCsvFilename(businessName, capturedAt);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
